/**
 * File Parser Utility
 * Extracts content from various file formats
 */
/**
 * Parse a text file
 */
function parseTextFile(content) {
    const lines = content.split('\n');
    const title = lines[0]?.trim() || 'Untitled';
    return {
        title,
        content: content.trim()
    };
}
/**
 * Parse a Markdown file
 */
function parseMarkdownFile(content) {
    // Extract title from first H1 or filename
    const h1Match = content.match(/^#\s+(.+)$/m);
    const title = h1Match ? h1Match[1].trim() : 'Untitled';
    return {
        title,
        content: content.trim()
    };
}
/**
 * Parse a DOCX file
 * Extracts text content from Word documents
 */
async function parseDocxFile(arrayBuffer) {
    try {
        const text = await extractTextFromDocx(arrayBuffer);
        const lines = text.split('\n');
        const title = lines[0]?.trim() || 'Untitled';
        return {
            title,
            content: text.trim()
        };
    }
    catch (error) {
        console.error('Failed to parse DOCX file:', error);
        // Fallback: try basic text extraction
        const text = new TextDecoder().decode(new Uint8Array(arrayBuffer));
        return {
            title: 'Imported Document',
            content: text
        };
    }
}
/**
 * Extract text from DOCX file
 * This is a simplified extraction that may not preserve all formatting
 */
async function extractTextFromDocx(arrayBuffer) {
    try {
        // Try using JSZip to extract content from DOCX (which is a ZIP file)
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        await zip.loadAsync(arrayBuffer);
        // DOCX files contain the text in document.xml
        const documentXml = await zip.file('word/document.xml')?.async('string');
        if (!documentXml) {
            throw new Error('No document.xml found in DOCX file');
        }
        // Extract text from XML (remove tags)
        const text = documentXml
            .replace(/<[^>]+>/g, '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/\s+/g, ' ')
            .trim();
        return text;
    }
    catch (error) {
        console.error('DOCX extraction failed:', error);
        throw new Error('Unable to parse DOCX file. Please try a different format.');
    }
}
/**
 * Parse any supported file type
 */
export async function parseFile(file) {
    const extension = file.name.split('.').pop()?.toLowerCase();
    try {
        if (extension === 'txt') {
            const text = await file.text();
            return parseTextFile(text);
        }
        else if (extension === 'md') {
            const text = await file.text();
            return parseMarkdownFile(text);
        }
        else if (extension === 'docx') {
            const arrayBuffer = await file.arrayBuffer();
            return parseDocxFile(arrayBuffer);
        }
        else {
            throw new Error(`Unsupported file format: ${extension}`);
        }
    }
    catch (error) {
        console.error('File parsing error:', error);
        throw error;
    }
}
/**
 * Get supported file types
 */
export function getSupportedFileTypes() {
    return ['.txt', '.md', '.docx'];
}
/**
 * Get file input accept attribute
 */
export function getFileInputAccept() {
    return '.txt,.md,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}
